import { Component, signal } from '@angular/core';
import {
	ActivityTypes,
	ContentText,
	StrideProgressItem,
	StrideStudentReportData,
	RubricLevel,
	Skill,
	TextUtils,
} from '@mira-avant/mtlibs';
import { NavigationService } from '../../../../../services/navigation.service';
import { AngularModule } from '../../../../../modules/angular.module';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { DisplayNamePipe } from '../../../../../pipes/display-name.pipe';
import { SkillProgressComponent } from './skill-progress/skill-progress.component';
import { CollapsibleSectionComponent } from './collapsible-section/collapsible-section.component';
import { WhitePanelComponent } from './white-panel/white-panel.component';
import { StrideAssessmentComponent } from './stride-assessment/stride-assessment.component';
import { CoachChatComponent } from '../../coach-chat/coach-chat.component';
import { WireframeReport } from '../wireframe-report/wireframe-report';
import { SkillService } from '../../../../../services/skill.service';
import { StrideReports } from '../../../../../services/stride-reports';
import { CoachService } from '../../../../../services/coach.service';

@Component({
	selector: 'app-stride-student-report',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		DisplayNamePipe,
		SkillProgressComponent,
		CollapsibleSectionComponent,
		WhitePanelComponent,
		StrideAssessmentComponent,
		CoachChatComponent,
		WireframeReport,
	],
	templateUrl: './stride-student-report.html',
	styleUrl: './stride-student-report.scss',
})
export class StrideStudentReport {
	report = signal<StrideStudentReportData | undefined>(undefined);
	studentUuid = signal<string | undefined>(undefined);
	selectedSkill = signal<string | undefined>(undefined);
	selectedSkillDescriptors = signal<string[]>([]);
	selectedRubricLevel = signal<RubricLevel | undefined>(undefined);
	selectedProgressItem = signal<StrideProgressItem | undefined>(undefined);
	containsVisualDescription = signal(false);
	sortedSkills = signal<Skill[]>([]);
	selectedActivityType = signal<ActivityTypes | undefined>(undefined);
	loadingActivityType = signal(false);

	private activityTypeRequestId = 0;

	constructor(
		private strideReportsService: StrideReports,
		private nav: NavigationService,
		private skillService: SkillService,
		private coachService: CoachService
	) {
		this.skillService.getSkills$().then((skills: Skill[]) => {
			this.sortedSkills.set(skills);
			this.selectFirstSkill();
		});

		this.loadReport();
	}

	private async loadReport() {
		const matrixParams = await this.nav.matrixParams$.getValue();

		const sectionUuid: string = matrixParams['sectionUuid'];
		const studentUuidVal = matrixParams['studentUuid'];

		this.studentUuid.set(studentUuidVal);

		const report = await this.strideReportsService.getStrideStudentReport$(studentUuidVal, sectionUuid);

		this.report.set(report);
	}

	private selectFirstSkill() {
		if (this.selectedSkill()) {
			return;
		}

		const firstSkill = this.sortedSkills()[0];
		if (!firstSkill?.code) {
			return;
		}

		this.selectSkill(firstSkill.code);
	}

	public selectSkill(skill: string) {
		this.selectProgressItem(undefined);
		this.selectedSkill.set(skill);
	}

	public selectProgressItem(item: StrideProgressItem | undefined) {
		this.activityTypeRequestId += 1;
		this.selectedProgressItem.set(item);
		this.selectedActivityType.set(undefined);

		this.containsVisualDescription.set(
			item ? TextUtils.containsVisualContent(item.activity.activity_text[0].description_html ?? '') : false
		);

		if (item) {
			void this.loadActivityType(item, this.activityTypeRequestId);
		}

		this.selectSkillDescriptors();
	}

	private async loadActivityType(item: StrideProgressItem, requestId: number) {
		const contentText = item.content.content_text?.[0] as ContentText | undefined;
		if (!contentText) {
			return;
		}

		try {
			this.loadingActivityType.set(true);
			const activityType = await this.coachService.getActivityType(contentText);

			if (requestId !== this.activityTypeRequestId) {
				return;
			}

			this.selectedActivityType.set(activityType);
		} catch (e: unknown) {
			console.error('Error loading activity type:', e);
			if (requestId === this.activityTypeRequestId) {
				this.selectedActivityType.set(undefined);
			}
		} finally {
			if (requestId === this.activityTypeRequestId) {
				this.loadingActivityType.set(false);
			}
		}
	}

	public isWireframeActivity(): boolean {
		return this.selectedActivityType() === ActivityTypes.WIREFRAME;
	}

	public isCoachActivity(): boolean {
		return !this.loadingActivityType() && this.selectedActivityType() !== ActivityTypes.WIREFRAME;
	}

	private selectSkillDescriptors() {
		const skill = this.selectedSkill();
		if (!skill) {
			return;
		}

		const reportVal = this.report();
		if (!reportVal) {
			return;
		}

		const firstLevelInRubric = reportVal.rubricLevels[reportVal.rubricLevels.length - 1];
		this.selectedRubricLevel.set(firstLevelInRubric);

		reportVal.rubricLevels.forEach(rubricLevel => {
			const studentSkillLevel = this.selectedProgressItem()?.contentRubricLevel || -1;

			if (rubricLevel.level !== studentSkillLevel) {
				return;
			}

			this.selectedRubricLevel.set(rubricLevel);

			rubricLevel.rubric_level_skill?.forEach(skillLevel => {
				if (skillLevel.skill_code === skill) {
					const descriptors =
						skillLevel.long_description
							?.split(/(?<=\w)\.(?=\s|$)/)
							.map(sentence => sentence.trim())
							.filter(sentence => sentence.length > 0)
							.map(sentence => (sentence.endsWith('.') ? sentence : sentence + '.')) || [];

					this.selectedSkillDescriptors.set(descriptors);
				}
			});
		});
	}

	public hasStrengths(): boolean {
		const item = this.selectedProgressItem();
		const assessment = item?.assessment;
		return (assessment?.strengths || []).length > 0;
	}

	public hasNeedsImprovement(): boolean {
		const item = this.selectedProgressItem();
		const assessment = item?.assessment;
		return (assessment?.needsImprovement || []).length > 0;
	}

	public hasStrideForward(): boolean {
		const item = this.selectedProgressItem();
		const assessment = item?.assessment;
		return (assessment?.strideForward || []).length > 0;
	}
}
